import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import dayTh from 'dayjs/locale/th';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import client from '../../api/client';
import './Profile.css';

dayjs.extend(buddhistEra);
dayjs.locale(dayTh);

const Profile = ({ permitdata, callback_signout }) => {
  const navigate = useNavigate();
  const [txFilter, setTxFilter] = useState('all'); // 'all', 'earn', 'spend'

  // Query wallet balance unconditionally
  const { data: walletData } = useQuery({
    queryKey: ['wallet-balance', permitdata?.shop_id],
    queryFn: async () => {
      if (!permitdata?.shop_id) return { balance: 0, total_earned: 0, total_spent: 0 };
      const formData = new FormData();
      formData.append('buyer_shopid', permitdata.shop_id);
      const res = await client.post('/api/order/wallet/balance', formData, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${permitdata?.token}`,
        },
      });
      return res.data?.status ? res.data : { balance: 0, total_earned: 0, total_spent: 0 };
    },
    enabled: Boolean(permitdata?.token && permitdata?.shop_id),
  });

  // Query wallet transactions unconditionally
  const { data: txData, isLoading: txLoading } = useQuery({
    queryKey: ['wallet-transactions', permitdata?.shop_id],
    queryFn: async () => {
      if (!permitdata?.shop_id) return [];
      const formData = new FormData();
      formData.append('buyer_shopid', permitdata.shop_id);
      const res = await client.post('/api/order/wallet/transactions', formData, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${permitdata?.token}`,
        },
      });
      return res.data?.status && Array.isArray(res.data.transactions) ? res.data.transactions : [];
    },
    enabled: Boolean(permitdata?.token && permitdata?.shop_id),
  });

  const walletBalance = Number(walletData?.balance || 0);
  const rawTransactions = useMemo(() => txData || [], [txData]);

  const filteredTransactions = useMemo(() => {
    if (txFilter === 'earn') {
      return rawTransactions.filter(
        (tx) =>
          tx.transaction_type === 'CASHBACK_EARN' ||
          tx.transaction_type === 'ORDER_REFUND' ||
          (tx.transaction_type === 'MANUAL_ADJUST' && tx.amount > 0),
      );
    }
    if (txFilter === 'spend') {
      return rawTransactions.filter(
        (tx) =>
          tx.transaction_type === 'ORDER_DISCOUNT' ||
          (tx.transaction_type === 'MANUAL_ADJUST' && tx.amount < 0),
      );
    }
    return rawTransactions;
  }, [rawTransactions, txFilter]);

  if (!permitdata) return null;

  return (
    <div className='profile-page font-th'>
      <div className='profile-layout-container'>
        {/* Left Column: User Account Card */}
        <div className='profile-col-user'>
          <div className='profile-user-card'>
            <div className='user-card-header'>
              <div className='user-avatar-wrap'>
                <div className='user-avatar-circle'>
                  <i className='fe-user'></i>
                </div>
              </div>
              <div className='user-header-info'>
                <h2 className='user-title-name'>{permitdata.shop_username || '-'}</h2>
                <div className='user-title-email'>{permitdata.shop_email || '-'}</div>
                {permitdata.shop_name && permitdata.shop_name !== '-' && (
                  <span className='user-badge-shop'>
                    <i className='fas fa-store mr-1'></i>
                    {permitdata.shop_name}
                  </span>
                )}
              </div>
            </div>

            <div className='user-card-body'>
              <div className='section-title-sm'>
                <i className='fas fa-id-card mr-2 text-primary'></i> ข้อมูลบัญชีผู้ใช้
              </div>

              <div className='user-info-rows'>
                <div className='info-row'>
                  <span className='info-label'>รหัสร้านค้า</span>
                  <span className='info-value font-weight-bold'>{permitdata.shop_id || '-'}</span>
                </div>
                <div className='info-row'>
                  <span className='info-label'>ชื่อผู้ใช้</span>
                  <span className='info-value'>{permitdata.shop_username || '-'}</span>
                </div>
                <div className='info-row'>
                  <span className='info-label'>ชื่อ - นามสกุล</span>
                  <span className='info-value'>
                    {permitdata.shop_firstname || '-'} {permitdata.shop_lastname || ''}
                  </span>
                </div>
                <div className='info-row'>
                  <span className='info-label'>เบอร์โทรศัพท์</span>
                  <span className='info-value'>{permitdata.shop_telno || '-'}</span>
                </div>
                <div className='info-row'>
                  <span className='info-label'>จังหวัด</span>
                  <span className='info-value'>{permitdata.shop_province || '-'}</span>
                </div>
                <div className='info-row-full'>
                  <span className='info-label'>ที่อยู่จัดส่ง</span>
                  <div className='info-value-address'>
                    {[
                      permitdata.shop_address,
                      permitdata.shop_subdistrict,
                      permitdata.shop_district,
                      permitdata.shop_province,
                      permitdata.shop_postcode,
                    ]
                      .filter((v) => v && v !== '-')
                      .join(' ') || '-'}
                  </div>
                </div>
              </div>

              <div className='user-contact-hint'>
                หากต้องการแก้ไขข้อมูลบัญชี ติดต่อ Line <span className='line-bold'>@meya</span>
              </div>

              <button
                type='button'
                className='btn-logout-user'
                onClick={() => {
                  callback_signout();
                  navigate('/');
                }}
              >
                <i className='fe-log-out mr-2'></i> ออกจากระบบ
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Meya Coins & Statement Dashboard */}
        <div className='profile-col-wallet'>
          <div className='profile-wallet-card'>
            {/* Coins Balance Hero Banner */}
            <div className='coins-hero-banner'>
              <div className='coins-hero-top'>
                <div className='d-flex align-items-center'>
                  <div className='coins-circle-icon mr-3'>
                    <i className='fas fa-coins'></i>
                  </div>
                  <div>
                    <h3 className='coins-hero-title'>กระเป๋าเหรียญสะสม Meya Coins</h3>
                    <div className='coins-hero-subtitle'>
                      ใช้เป็นส่วนลดเงินสดได้ทันทีในการสั่งซื้อ (1 Coin = 1 บาท)
                    </div>
                  </div>
                </div>
              </div>

              <div className='coins-hero-balance-row'>
                <div className='balance-box'>
                  <div className='balance-box-label'>ยอดเหรียญคงเหลือ</div>
                  <div className='balance-box-num'>
                    ฿{walletBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </div>
                </div>

                <div className='stats-boxes'>
                  <div className='stat-pill-box'>
                    <span className='pill-label'>ได้รับสะสมทั้งหมด</span>
                    <strong className='pill-val text-success'>
                      +฿
                      {Number(walletData?.total_earned || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                  <div className='stat-pill-box'>
                    <span className='pill-label'>ใช้ไปแล้วทั้งหมด</span>
                    <strong className='pill-val text-muted'>
                      ฿
                      {Number(walletData?.total_spent || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </strong>
                  </div>
                </div>
              </div>
            </div>

            {/* Statement History Ledger Section */}
            <div className='wallet-history-section'>
              <div className='history-header'>
                <div className='d-flex align-items-center'>
                  <i className='fas fa-history text-primary mr-2' style={{ fontSize: '18px' }}></i>
                  <h4 className='history-title'>ประวัติความเคลื่อนไหวเหรียญ (Ledger Statement)</h4>
                </div>

                <div className='history-filter-tabs'>
                  <button
                    type='button'
                    className={`filter-tab-btn ${txFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setTxFilter('all')}
                  >
                    ทั้งหมด ({rawTransactions.length})
                  </button>
                  <button
                    type='button'
                    className={`filter-tab-btn ${txFilter === 'earn' ? 'active' : ''}`}
                    onClick={() => setTxFilter('earn')}
                  >
                    ได้รับ (+)
                  </button>
                  <button
                    type='button'
                    className={`filter-tab-btn ${txFilter === 'spend' ? 'active' : ''}`}
                    onClick={() => setTxFilter('spend')}
                  >
                    ใช้ไป (-)
                  </button>
                </div>
              </div>

              <div className='history-content-body'>
                {txLoading ? (
                  <div className='loading-state-box'>
                    <i className='fas fa-spinner fa-spin mr-2'></i> กำลังโหลดประวัติเหรียญ...
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div className='empty-state-box'>
                    <i className='fas fa-coins empty-coins-icon'></i>
                    <div className='empty-title'>ยังไม่มีรายการประวัติเหรียญ</div>
                    <div className='empty-subtitle'>
                      เมื่อคุณสั่งซื้อสินค้าหรือใช้โค้ดแคชแบ็ก เหรียญจะถูกบันทึกลงที่นี่โดยอัตโนมัติ
                    </div>
                  </div>
                ) : (
                  <div className='transactions-list-container'>
                    {filteredTransactions.map((tx) => {
                      const isEarn =
                        tx.transaction_type === 'CASHBACK_EARN' ||
                        tx.transaction_type === 'ORDER_REFUND' ||
                        (tx.transaction_type === 'MANUAL_ADJUST' && tx.amount > 0);
                      let badgeText = 'รับเงินคืน Cashback';
                      let badgeClass = 'tx-badge-earn';
                      if (tx.transaction_type === 'ORDER_DISCOUNT') {
                        badgeText = 'ใช้เป็นส่วนลด';
                        badgeClass = 'tx-badge-spend';
                      } else if (tx.transaction_type === 'ORDER_REFUND') {
                        badgeText = 'คืนเหรียญคำสั่งซื้อ';
                        badgeClass = 'tx-badge-refund';
                      } else if (tx.transaction_type === 'MANUAL_ADJUST') {
                        badgeText = 'ปรับปรุงยอดเหรียญ';
                        badgeClass = 'tx-badge-admin';
                      }

                      return (
                        <div key={tx.transaction_id} className='tx-ledger-item'>
                          <div className='tx-left-block'>
                            <div className='d-flex align-items-center mb-1'>
                              <span className={`tx-type-badge ${badgeClass}`}>{badgeText}</span>
                              {tx.order_id && (
                                <span className='tx-order-ref'>ออเดอร์: {tx.order_id}</span>
                              )}
                            </div>
                            <div className='tx-description-text'>{tx.description || '-'}</div>
                            <div className='tx-datetime-text'>
                              <i className='far fa-clock mr-1'></i>
                              {dayjs(tx.createtime).format('DD MMMM YYYY, HH:mm น.')}
                            </div>
                          </div>

                          <div className='tx-right-block'>
                            <div
                              className={`tx-amount-display ${
                                isEarn ? 'tx-amount-plus' : 'tx-amount-minus'
                              }`}
                            >
                              {isEarn ? '+' : '-'}฿
                              {Math.abs(tx.amount).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </div>
                            <div className='tx-balance-after-text'>
                              ยอดคงเหลือ ฿
                              {Number(tx.balance_after).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Profile.propTypes = {
  permitdata: PropTypes.object,
  callback_signout: PropTypes.func.isRequired,
};

Profile.defaultProps = {
  permitdata: null,
};

export default Profile;
